import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | undefined} */
  #loadObserver;
  /** @type {IntersectionObserver | undefined} */
  #playObserver;
  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {HTMLVideoElement | undefined} */
  #video;
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #nearViewport = false;
  #visible = false;

  connectedCallback() {
    super.connectedCallback();
    this.#observe();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observe();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #cleanup() {
    this.#loadObserver?.disconnect();
    this.#playObserver?.disconnect();
    this.#listeners?.abort();
    this.#video?.pause();
  }

  #observe() {
    this.#cleanup();
    this.#video = this.refs.videoElement;
    // Playback is controlled explicitly so load() cannot start autoplay offscreen.
    this.#video.autoplay = false;
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.#video.addEventListener('playing', () => this.setAttribute('data-playing', ''), { signal });
    document.addEventListener('visibilitychange', this.#syncPlayback, { signal });
    this.#motion.addEventListener('change', this.#syncPlayback, { signal });

    // Keep media editable and avoid leaving a blank background without a poster.
    const immediate = Shopify.designMode || !this.hasAttribute('data-has-poster') || !('IntersectionObserver' in window);
    this.#nearViewport = this.#visible = Boolean(immediate);
    if (immediate) {
      this.#syncPlayback();
      return;
    }

    this.#loadObserver = new IntersectionObserver(([entry]) => {
      this.#nearViewport = entry.isIntersecting;
      this.#syncPlayback();
    }, { rootMargin: '400px 0px' });
    this.#playObserver = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#syncPlayback();
    });
    this.#loadObserver.observe(this);
    this.#playObserver.observe(this);
  }

  #syncPlayback = () => {
    const video = this.#video;
    if (!video || !this.isConnected) return;

    if (document.hidden || (this.#motion.matches && this.hasAttribute('data-has-poster'))) {
      video.pause();
      return;
    }

    if (this.#nearViewport || this.#visible) this.#loadSources();
    if (this.#visible) {
      // Browsers may reject autoplay. Leave the poster visible in that case.
      if (video.paused) video.play().catch(() => {});
    } else {
      video.pause();
    }
  };

  #loadSources() {
    const { videoSources, videoElement } = this.refs;
    let changed = false;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource && source.getAttribute('src') !== videoSource) {
        source.setAttribute('src', videoSource);
        changed = true;
      }
    }

    if (changed) {
      this.removeAttribute('data-playing');
      videoElement.preload = 'auto';
      videoElement.load();
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
